import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClientLike } from './people';

export interface WfhRequest {
  id: string;
  tenant_id: string;
  employee_id: string;
  date: string;
  end_date: string | null;
  reason: string | null;
  status: 'pending' | 'approved' | 'rejected';
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface WfhPolicy {
  id: string;
  tenant_id: string;
  max_days_per_week: number;
  auto_approve: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateWfhRequestInput {
  date: string;
  end_date?: string | null;
  reason?: string | null;
}

export interface ReviewWfhRequestInput {
  id: string;
  status: 'approved' | 'rejected';
}

export interface UpdateWfhPolicyInput {
  max_days_per_week: number;
  auto_approve: boolean;
}

export const wfhKeys = {
  all: ['wfh'] as const,
  requests: (tenantId: string, filters?: { status?: string; employee_id?: string }) =>
    [...wfhKeys.all, 'requests', tenantId, filters ?? {}] as const,
  policy: (tenantId: string) => [...wfhKeys.all, 'policy', tenantId] as const,
} as const;

export function useWfhRequests(
  supabase: SupabaseClientLike,
  tenantId: string,
  filters?: { status?: string; employee_id?: string },
) {
  return useQuery({
    queryKey: wfhKeys.requests(tenantId, filters),
    queryFn: async () => {
      // @ts-ignore dynamic supabase client
      let q = (supabase as unknown as { from: (t: string) => any })
        .from('wfh_requests')
        // @ts-ignore dynamic
        .select('*')
        .eq('tenant_id', tenantId)
        .order('date', { ascending: false });
      if (filters?.status) {
        // @ts-ignore dynamic
        q = q.eq('status', filters.status);
      }
      if (filters?.employee_id) {
        // @ts-ignore dynamic
        q = q.eq('employee_id', filters.employee_id);
      }
      const { data, error } = await q;
      if (error) throw error;
      return data as WfhRequest[];
    },
    enabled: Boolean(tenantId),
  });
}

export function useCreateWfhRequest(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateWfhRequestInput) => {
      const { data, error } = await (supabase as unknown as { from: (t: string) => any })
        .from('wfh_requests')
        // @ts-ignore dynamic
        .insert({ ...input, tenant_id: tenantId })
        .select()
        .single();
      if (error) throw error;
      return data as WfhRequest;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: wfhKeys.all }),
  });
}

export function useReviewWfhRequest(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ReviewWfhRequestInput) => {
      const { data, error } = await (supabase as unknown as { from: (t: string) => any })
        .from('wfh_requests')
        // @ts-ignore dynamic
        .update({
          status: input.status,
          reviewed_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', input.id)
        .eq('tenant_id', tenantId)
        .select()
        .single();
      if (error) throw error;
      return data as WfhRequest;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: wfhKeys.all }),
  });
}

export function useDeleteWfhRequest(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase as unknown as { from: (t: string) => any })
        .from('wfh_requests')
        // @ts-ignore dynamic
        .delete()
        .eq('id', id)
        .eq('tenant_id', tenantId);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: wfhKeys.all }),
  });
}

export function useWfhPolicy(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: wfhKeys.policy(tenantId),
    queryFn: async () => {
      const { data, error } = await (supabase as unknown as { from: (t: string) => any })
        .from('wfh_policies')
        // @ts-ignore dynamic
        .select('*')
        .eq('tenant_id', tenantId)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as WfhPolicy | null;
    },
    enabled: Boolean(tenantId),
  });
}

export function useUpdateWfhPolicy(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateWfhPolicyInput) => {
      const { data, error } = await (supabase as unknown as { from: (t: string) => any })
        .from('wfh_policies')
        // @ts-ignore dynamic
        .upsert(
          { ...input, tenant_id: tenantId, updated_at: new Date().toISOString() },
          { onConflict: 'tenant_id' },
        )
        .select()
        .single();
      if (error) throw error;
      return data as WfhPolicy;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: wfhKeys.policy(tenantId) }),
  });
}
